'use client'

import { QUERY_KEYS } from '@/hooks/react-query/constants'
import { useRouter } from '@/i18n/navigation'
import type { TUserProfile } from '@/types/auth'
import { useQueryClient } from '@tanstack/react-query'
import { useLocale } from 'next-intl'

type UsePostLoginParams = {
  user?: TUserProfile | null
  redirectUrl?: string | null
}

/**
 * Xử lý hậu đăng nhập: cập nhật cache user tức thì, invalidate những query phụ thuộc,
 * và điều hướng nếu có redirectUrl. Tránh dùng router.refresh() để giảm giật lag.
 */
export function usePostLogin() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const locale = useLocale()

  return ({ user, redirectUrl }: UsePostLoginParams = {}) => {
    // Cập nhật cache user ngay để UI phản hồi tức thì
    if (user) {
      queryClient.setQueryData([QUERY_KEYS.USER], user)
    }

    // Invalidate có chọn lọc các query phụ thuộc trạng thái đăng nhập
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CART] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COUNT_CART_ITEM] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WISHLIST] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COOL_CLUB_MEMBER] })

    // Đảm bảo user được refetch ngay lập tức nếu chưa có payload user
    if (!user) {
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.USER] })
    } else {
      // Nếu có user tức thì, kick refetch cool-club để cập nhật avatar nhanh hơn
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.COOL_CLUB_MEMBER] })
    }

    // Điều hướng nếu có URL đích, tránh refresh toàn trang
    if (redirectUrl) {
      const isInternal =
        redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')
      if (isInternal) {
        router.replace(redirectUrl, { locale })
      } else {
        window.location.href = redirectUrl
      }
    }
  }
}
